import { defaultConfig } from '@chakra-ui/react/preset';
import { createSystem, defineConfig } from '@chakra-ui/react/styled-system';

const config = defineConfig({
  theme: {
    breakpoints: {
      sm: '320px',
      md: '768px',
      lg: '960px',
      xl: '1200px',
    },
    tokens: {
      colors: {
        primary: {
          50: { value: '#e3f7f1' },
          100: { value: '#c2eadf' },
          200: { value: '#9fdcc9' },
          300: { value: '#7ccfb3' },
          400: { value: '#56c29d' },
          500: { value: '#0C8A6F' },
          600: { value: '#0A755F' },
          700: { value: '#075f50' },
          800: { value: '#054a41' },
          900: { value: '#033430' },
        },
        secondary: {
          50: { value: '#e4ebf1' },
          100: { value: '#c7d7e3' },
          200: { value: '#a8c3d4' },
          300: { value: '#89afc5' },
          400: { value: '#6b9bb6' },
          500: { value: '#215673' },
          600: { value: '#1c4a64' },
          700: { value: '#163d54' },
          800: { value: '#113144' },
          900: { value: '#0c2434' },
        },
        error: {
          50: { value: '#fff2f2' },
          100: { value: '#fedede' },
          200: { value: '#fcbebe' },
          300: { value: '#f89d9d' },
          400: { value: '#f57d7d' },
          500: { value: '#F06A6A' },
          600: { value: '#d85e5e' },
          700: { value: '#b85151' },
          800: { value: '#974444' },
          900: { value: '#6f3333' },
        },
        success: {
          50: { value: '#f0f9eb' },
          100: { value: '#e1f2d4' },
          200: { value: '#cfe6b9' },
          300: { value: '#b0d99e' },
          400: { value: '#91cc83' },
          500: { value: '#00B894' },
          600: { value: '#00a181' },
          700: { value: '#00856d' },
          800: { value: '#006959' },
          900: { value: '#004d44' },
        },
        warning: {
          50: { value: '#fff8e6' },
          100: { value: '#ffe8b3' },
          200: { value: '#ffd980' },
          300: { value: '#ffca4d' },
          400: { value: '#ffbb1a' },
          500: { value: '#ffaa00' },
          600: { value: '#e69500' },
          700: { value: '#c28000' },
          800: { value: '#a16a00' },
          900: { value: '#815500' },
        },
      },
      fonts: {
        heading: { value: '"Nunito Sans", system-ui, -apple-system, sans-serif' },
        body: { value: '"Nunito Sans", system-ui, -apple-system, sans-serif' },
      },
    },
    semanticTokens: {
      colors: {
        bg: {
          DEFAULT: {
            value: { _light: '{colors.gray.50}', _dark: '{colors.gray.900}' },
          },
        },
        fg: {
          DEFAULT: {
            value: { _light: '{colors.gray.900}', _dark: '{colors.gray.300}' },
          },
        },
        primary: {
          contrast: { value: 'white' },
          bg: {
            value: { _light: '{colors.primary.50}', _dark: '{colors.primary.900}' },
          },
          fg: {
            value: { _light: '{colors.primary.700}', _dark: '{colors.primary.300}' },
          },
          subtle: {
            value: { _light: '{colors.primary.100}', _dark: '{colors.primary.900}' },
          },
          muted: {
            value: { _light: '{colors.primary.200}', _dark: '{colors.primary.800}' },
          },
          emphasized: {
            value: { _light: '{colors.primary.300}', _dark: '{colors.primary.700}' },
          },
          solid: {
            value: { _light: '{colors.primary.600}', _dark: '{colors.primary.500}' },
          },
          focusRing: { value: '{colors.primary.500}' },
          border: {
            value: { _light: '{colors.primary.500}', _dark: '{colors.primary.400}' },
          },
        },
        secondary: {
          contrast: { value: 'white' },
          bg: {
            value: { _light: '{colors.secondary.50}', _dark: '{colors.secondary.900}' },
          },
          fg: {
            value: { _light: '{colors.secondary.700}', _dark: '{colors.secondary.300}' },
          },
          subtle: {
            value: { _light: '{colors.secondary.100}', _dark: '{colors.secondary.900}' },
          },
          muted: {
            value: { _light: '{colors.secondary.200}', _dark: '{colors.secondary.800}' },
          },
          emphasized: {
            value: { _light: '{colors.secondary.300}', _dark: '{colors.secondary.700}' },
          },
          solid: {
            value: { _light: '{colors.secondary.600}', _dark: '{colors.secondary.500}' },
          },
          focusRing: { value: '{colors.secondary.500}' },
          border: {
            value: { _light: '{colors.secondary.500}', _dark: '{colors.secondary.400}' },
          },
        },
        backButton: {
          contrast: { value: 'white' },
          fg: {
            value: { _light: '{colors.gray.700}', _dark: '{colors.gray.400}' },
          },
          subtle: {
            value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
          },
          muted: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
          },
          emphasized: {
            value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
          },
          solid: {
            value: { _light: '{colors.gray.600}', _dark: '{colors.gray.500}' },
          },
          focusRing: { value: '{colors.gray.500}' },
          border: {
            value: { _light: '{colors.gray.400}', _dark: '{colors.gray.500}' },
          },
        },
        error: {
          contrast: { value: 'white' },
          bg: {
            value: { _light: '{colors.error.50}', _dark: '{colors.error.900}' },
          },
          fg: {
            value: { _light: '{colors.error.700}', _dark: '{colors.error.300}' },
          },
          subtle: {
            value: { _light: '{colors.error.100}', _dark: '{colors.error.900}' },
          },
          muted: {
            value: { _light: '{colors.error.200}', _dark: '{colors.error.800}' },
          },
          emphasized: {
            value: { _light: '{colors.error.300}', _dark: '{colors.error.700}' },
          },
          solid: {
            value: { _light: '{colors.error.600}', _dark: '{colors.error.500}' },
          },
          focusRing: { value: '{colors.error.500}' },
          border: {
            value: { _light: '{colors.error.500}', _dark: '{colors.error.400}' },
          },
        },
        success: {
          contrast: { value: 'white' },
          bg: {
            value: { _light: '{colors.success.50}', _dark: '{colors.success.900}' },
          },
          fg: {
            value: { _light: '{colors.success.700}', _dark: '{colors.success.300}' },
          },
          subtle: {
            value: { _light: '{colors.success.100}', _dark: '{colors.success.900}' },
          },
          muted: {
            value: { _light: '{colors.success.200}', _dark: '{colors.success.800}' },
          },
          emphasized: {
            value: { _light: '{colors.success.300}', _dark: '{colors.success.700}' },
          },
          solid: {
            value: { _light: '{colors.success.600}', _dark: '{colors.success.500}' },
          },
          focusRing: { value: '{colors.success.500}' },
          border: {
            value: { _light: '{colors.success.500}', _dark: '{colors.success.400}' },
          },
        },
        warning: {
          contrast: { value: 'white' },
          fg: {
            value: { _light: '{colors.warning.700}', _dark: '{colors.warning.300}' },
          },
          subtle: {
            value: { _light: '{colors.warning.100}', _dark: '{colors.warning.900}' },
          },
          muted: {
            value: { _light: '{colors.warning.200}', _dark: '{colors.warning.800}' },
          },
          emphasized: {
            value: { _light: '{colors.warning.300}', _dark: '{colors.warning.700}' },
          },
          solid: {
            value: { _light: '{colors.warning.600}', _dark: '{colors.warning.500}' },
          },
          focusRing: { value: '{colors.warning.500}' },
          border: {
            value: { _light: '{colors.warning.500}', _dark: '{colors.warning.400}' },
          },
        },
        card: {
          bg: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.800}' },
          },
          border: {
            value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
          },
        },
        global: {
          bg: {
            DEFAULT: {
              value: { _light: '{colors.gray.50}', _dark: '{colors.gray.900}' },
            },
          },
          border: {
            DEFAULT: {
              value: { _light: '{colors.gray.200}', _dark: '{colors.gray.600}' },
            },
          },
          label: {
            DEFAULT: {
              value: { _light: '{colors.gray.700}', _dark: '{colors.gray.400}' },
            },
          },
        },
        sidebar: {
          bg: {
            DEFAULT: {
              value: { _light: '{colors.gray.800}', _dark: '{colors.gray.700}' },
            },
          },
          fg: {
            DEFAULT: {
              value: { _light: 'white', _dark: '{colors.gray.100}' },
            },
          },
          activeBg: {
            DEFAULT: {
              value: { _light: '{colors.gray.700}', _dark: '{colors.gray.600}' },
            },
          },
          hoverBg: {
            DEFAULT: {
              value: { _light: '{colors.gray.700}', _dark: '{colors.gray.600}' },
            },
          },
          iconButtonBg: {
            DEFAULT: {
              value: { _light: 'white', _dark: '{colors.gray.800}' },
            },
          },
          iconButtonFg: {
            DEFAULT: {
              value: { _light: '{colors.gray.900}', _dark: 'white' },
            },
          },
          iconButtonBorder: {
            DEFAULT: {
              value: { _light: '{colors.gray.200}', _dark: '{colors.gray.600}' },
            },
          },
        },
        divider: {
          DEFAULT: {
            value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
          },
        },
        lighterText: {
          DEFAULT: {
            value: { _light: '{colors.gray.500}', _dark: '{colors.gray.50}' },
          },
        },
        loadingBg: {
          DEFAULT: {
            value: { _light: '{colors.blackAlpha.300}', _dark: '{colors.blackAlpha.600}' },
          },
        },
      },
    },
  },
  globalCss: {
    body: {
      bg: { _light: 'gray.50', _dark: 'gray.900' },
      color: { _light: 'gray.900', _dark: 'gray.300' },
    },
  },
});

export default createSystem(defaultConfig, config);
